/**
 * Plays a subtle notification sound when new feedback arrives
 * Uses Web Audio API to generate a pleasant double-beep tone
 * @param volume - Volume level (0-100), defaults to 30
 */
export const playNotificationSound = (volume: number = 30): void => {
  try {
    // Check if browser supports Web Audio API
    const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContext) {
      // Web Audio API not supported - silent failure is acceptable
      return;
    }

    const audioContext = new AudioContext();
    
    // Create a gain node for volume control
    const gainNode = audioContext.createGain();
    gainNode.connect(audioContext.destination);
    // Convert volume from 0-100 to 0-1 for Web Audio API
    const normalizedVolume = Math.max(0, Math.min(100, volume)) / 100;
    gainNode.gain.value = normalizedVolume;

    // Function to play a single beep
    const playBeep = (frequency: number, startTime: number, duration: number) => {
      const oscillator = audioContext.createOscillator();
      oscillator.connect(gainNode);
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine'; // Sine wave for a smooth, pleasant tone
      
      // Add fade in/out to prevent clicking sounds
      const normalizedVolume = Math.max(0, Math.min(100, volume)) / 100;
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(normalizedVolume, startTime + 0.01);
      gainNode.gain.linearRampToValueAtTime(0, startTime + duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };

    // Create a pleasant double-beep notification sound
    const now = audioContext.currentTime;
    playBeep(600, now, 0.1); // First beep: 600Hz for 100ms
    playBeep(800, now + 0.15, 0.1); // Second beep: 800Hz for 100ms (starts 150ms after first)

    // Clean up the audio context after the sound finishes
    setTimeout(() => {
      audioContext.close();
    }, 500);
  } catch (error) {
    // Silently fail if audio playback is blocked or fails
    // This can happen due to browser autoplay policies
    console.debug('Notification sound failed to play:', error);
  }
};
